import React, { useEffect, useMemo, useState } from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    Row,
    Table,
    Spinner,
    Alert,
    Button,
} from "reactstrap";

import Chart from "react-apexcharts";

// IMPORTANT:
// Change this path according to your actual API file location.
import { fetchPortalLeads } from "../../helpers/userportal_api";


const PortalDashboard = () => {

    // =========================================================
    // STATE
    // =========================================================

    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [lastUpdated, setLastUpdated] = useState(null);


    // =========================================================
    // FETCH ALL LEADS
    // =========================================================

    const loadDashboardData = async () => {

        try {

            setLoading(true);
            setError("");

            let allLeads = [];
            let page = 1;

            // Fetch first page
            const firstResponse = await fetchPortalLeads({
                page: 1,
                pageLimit: 100,
                order: "DESC",
                orderBy: "createdtime",
                search: "",
            });

            const totalCount = Number(firstResponse?.count || 0);

            allLeads = Array.isArray(firstResponse?.data)
                ? [...firstResponse.data]
                : [];

            const pageLimit = Number(
                firstResponse?.pageLimit || 100
            );

            // Calculate number of pages
            const totalPages = Math.ceil(
                totalCount / pageLimit
            );

            // Fetch remaining pages
            for (page = 2; page <= totalPages; page++) {

                const response = await fetchPortalLeads({
                    page,
                    pageLimit,
                    order: "DESC",
                    orderBy: "createdtime",
                    search: "",
                });

                if (Array.isArray(response?.data)) {
                    allLeads = [
                        ...allLeads,
                        ...response.data,
                    ];
                }
            }

            setLeads(allLeads);

            setLastUpdated(new Date());

        } catch (err) {

            console.error(
                "Dashboard Leads Error:",
                err
            );

            setError(
                err?.message ||
                "Unable to load dashboard data."
            );

        } finally {

            setLoading(false);
        }
    };


    // =========================================================
    // LOAD DASHBOARD
    // =========================================================

    useEffect(() => {

        loadDashboardData();

    }, []);


    // =========================================================
    // HELPER FUNCTIONS
    // =========================================================

    const getLeadName = (lead) => {

        const firstName =
            lead?.firstname ||
            lead?.first_name ||
            "";

        const lastName =
            lead?.lastname ||
            lead?.last_name ||
            "";

        const fullName =
            lead?.name ||
            lead?.leadname ||
            lead?.lead_name ||
            "";

        if (fullName) {
            return fullName;
        }

        return `${firstName} ${lastName}`.trim() || "Unnamed Lead";
    };


    const getLeadStatus = (lead) => {

        return (
            lead?.leadstatus ||
            lead?.lead_status ||
            lead?.status ||
            "Unknown"
        );
    };


    const getLeadCompany = (lead) => {

        return (
            lead?.company ||
            lead?.organization ||
            lead?.accountname ||
            "-"
        );
    };


    const getLeadEmail = (lead) => {

        return (
            lead?.email ||
            lead?.email1 ||
            "-"
        );
    };


    const getLeadPhone = (lead) => {

        return (
            lead?.phone ||
            lead?.mobile ||
            lead?.mobilephone ||
            "-"
        );
    };


    const getLeadCourse = (lead) => {

        return (
            lead?.cf_907 ||
            lead?.course ||
            "-"
        );
    };


    const getLeadDate = (lead) => {

        return (
            lead?.createdtime ||
            lead?.created_time ||
            lead?.createdDate ||
            lead?.created_at ||
            lead?.createddate ||
            ""
        );
    };


    const getStatusKey = (status) => {

        const normalized = String(status || "")
            .trim()
            .toLowerCase();

        if (
            normalized.includes("new")
        ) {
            return "New";
        }

        if (
            normalized.includes("progress") ||
            normalized.includes("assigned") ||
            normalized.includes("contact") ||
            normalized.includes("working")
        ) {
            return "In Progress";
        }

        if (
            normalized.includes("closed") ||
            normalized.includes("converted") ||
            normalized.includes("completed")
        ) {
            return "Closed";
        }

        if (
            normalized.includes("lost") ||
            normalized.includes("dead")
        ) {
            return "Lost";
        }

        if (
            normalized.includes("qualified")
        ) {
            return "Qualified";
        }

        return status || "Unknown";
    };


    const parseDate = (value) => {

        if (!value) {
            return null;
        }

        const date = new Date(value);

        if (Number.isNaN(date.getTime())) {
            return null;
        }

        return date;
    };


    // =========================================================
    // CURRENT YEAR
    // =========================================================

    const currentYear = new Date().getFullYear();


    // =========================================================
    // DASHBOARD STATISTICS
    // =========================================================

    const statistics = useMemo(() => {

        let newCount = 0;
        let progressCount = 0;
        let closedCount = 0;

        leads.forEach((lead) => {

            const status = getStatusKey(
                getLeadStatus(lead)
            );

            if (status === "New") {
                newCount++;
            }

            if (status === "In Progress") {
                progressCount++;
            }

            if (status === "Closed") {
                closedCount++;
            }

        });

        return {

            total: leads.length,

            new: newCount,

            progress: progressCount,

            closed: closedCount,
        };

    }, [leads]);


    // =========================================================
    // STATUS DISTRIBUTION
    // =========================================================

    const statusDistribution = useMemo(() => {

        const statusMap = {};

        leads.forEach((lead) => {

            const status = getStatusKey(
                getLeadStatus(lead)
            );

            statusMap[status] =
                (statusMap[status] || 0) + 1;

        });

        return Object.entries(statusMap)
            .sort((a, b) => b[1] - a[1]);

    }, [leads]);


    // =========================================================
    // MONTHLY LEADS
    // =========================================================

    const monthlyData = useMemo(() => {

        const months = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];

        const counts = new Array(12).fill(0);

        leads.forEach((lead) => {

            const date = parseDate(
                getLeadDate(lead)
            );

            if (!date) {
                return;
            }

            if (
                date.getFullYear() === currentYear
            ) {

                const month =
                    date.getMonth();

                counts[month]++;
            }

        });

        return {
            months,
            counts,
        };

    }, [leads, currentYear]);


    // =========================================================
    // RECENT LEADS
    // =========================================================

    const recentLeads = useMemo(() => {

        return [...leads]
            .sort((a, b) => {

                const dateA =
                    parseDate(getLeadDate(a));

                const dateB =
                    parseDate(getLeadDate(b));

                if (!dateA && !dateB) {
                    return 0;
                }

                if (!dateA) {
                    return 1;
                }

                if (!dateB) {
                    return -1;
                }

                return dateB - dateA;
            })
            .slice(0, 10);

    }, [leads]);


    // =========================================================
    // STATUS CHART
    // =========================================================

    const statusChart = useMemo(() => {

        return {

            series: statusDistribution.map(
                ([, count]) => count
            ),

            options: {

                chart: {
                    type: "donut",
                    height: 320,
                },

                labels: statusDistribution.map(
                    ([status]) => status
                ),

                legend: {
                    position: "bottom",
                    fontSize: "13px",
                },

                dataLabels: {
                    enabled: true,
                },

                stroke: {
                    width: 2,
                },

                plotOptions: {

                    pie: {

                        donut: {

                            size: "68%",

                            labels: {

                                show: true,

                                total: {

                                    show: true,

                                    label: "Total Leads",

                                    formatter: () =>
                                        leads.length,
                                },
                            },
                        },
                    },
                },

                tooltip: {

                    y: {

                        formatter: (value) =>
                            `${value} Leads`,
                    },
                },

                responsive: [

                    {

                        breakpoint: 768,

                        options: {

                            chart: {
                                height: 280,
                            },

                            legend: {
                                position: "bottom",
                            },
                        },
                    },
                ],
            },
        };

    }, [
        statusDistribution,
        leads.length,
    ]);


    // =========================================================
    // MONTHLY CHART
    // =========================================================

    const monthlyChart = useMemo(() => {

        return {

            series: [

                {
                    name: "Leads",
                    data: monthlyData.counts,
                },

            ],

            options: {

                chart: {

                    type: "area",

                    height: 320,

                    toolbar: {
                        show: false,
                    },

                    zoom: {
                        enabled: false,
                    },
                },

                stroke: {

                    curve: "smooth",

                    width: 3,
                },

                xaxis: {

                    categories:
                        monthlyData.months,

                    labels: {

                        style: {
                            fontSize: "11px",
                        },
                    },
                },

                yaxis: {

                    min: 0,

                    forceNiceScale: true,

                    labels: {

                        formatter: (value) =>
                            Math.round(value),
                    },
                },

                dataLabels: {
                    enabled: false,
                },

                fill: {

                    type: "gradient",

                    gradient: {

                        opacityFrom: 0.35,

                        opacityTo: 0.05,
                    },
                },

                tooltip: {

                    y: {

                        formatter: (value) =>
                            `${value} Leads`,
                    },
                },

                responsive: [

                    {

                        breakpoint: 768,

                        options: {

                            chart: {
                                height: 280,
                            },

                            xaxis: {

                                labels: {

                                    rotate: -45,

                                    style: {
                                        fontSize: "10px",
                                    },
                                },
                            },
                        },
                    },

                ],
            },
        };

    }, [monthlyData]);


    // =========================================================
    // STATUS BADGE
    // =========================================================

    const getStatusClass = (status) => {

        const normalized =
            String(status || "")
                .toLowerCase();

        if (
            normalized.includes("closed") ||
            normalized.includes("converted")
        ) {
            return "bg-success-subtle text-success";
        }

        if (
            normalized.includes("progress") ||
            normalized.includes("working") ||
            normalized.includes("assigned")
        ) {
            return "bg-warning-subtle text-warning";
        }

        if (
            normalized.includes("lost") ||
            normalized.includes("dead")
        ) {
            return "bg-danger-subtle text-danger";
        }

        if (
            normalized.includes("qualified")
        ) {
            return "bg-info-subtle text-info";
        }

        return "bg-primary-subtle text-primary";
    };


    // =========================================================
    // STAT CARDS
    // =========================================================

    const stats = [

        {
            title: "Total Leads",
            value: statistics.total,
            icon: "ri-user-line",
            className: "primary",
        },

        {
            title: "New Leads",
            value: statistics.new,
            icon: "ri-user-add-line",
            className: "info",
        },

        {
            title: "In Progress",
            value: statistics.progress,
            icon: "ri-loader-4-line",
            className: "warning",
        },

        {
            title: "Closed Leads",
            value: statistics.closed,
            icon: "ri-checkbox-circle-line",
            className: "success",
        },

    ];


    // =========================================================
    // FORMAT DATE
    // =========================================================

    const formatDate = (value) => {

        const date = parseDate(value);

        if (!date) {
            return "-";
        }

        return date.toLocaleDateString(
            "en-IN",
            {
                day: "2-digit",
                month: "short",
                year: "numeric",
            }
        );
    };


    // =========================================================
    // LOADING
    // =========================================================

    if (loading) {

        return (

            <React.Fragment>

                <div className="page-content">

                    <Container fluid>

                        <div
                            className="d-flex justify-content-center align-items-center"
                            style={{
                                minHeight: "400px",
                            }}
                        >

                            <div className="text-center">

                                <Spinner
                                    color="primary"
                                    className="mb-3"
                                />

                                <div className="text-muted">
                                    Loading dashboard...
                                </div>

                            </div>

                        </div>

                    </Container>

                </div>

            </React.Fragment>

        );
    }


    // =========================================================
    // MAIN UI
    // =========================================================

    return (

        <React.Fragment>

            <div className="page-content">

                <Container fluid>

                    {/* =================================================
                        PAGE HEADER
                    ================================================= */}

                    <Row>

                        <Col xs={12}>

                            <div className="page-title-box d-sm-flex align-items-center justify-content-between">

                                <div>

                                    <h4 className="mb-sm-0">
                                        Dashboard
                                    </h4>

                                    <p className="text-muted mb-0 mt-1">
                                        Current CRM lead overview
                                    </p>

                                </div>

                                <div className="d-flex align-items-center gap-2">

                                    {lastUpdated && (

                                        <small className="text-muted d-none d-md-inline-block">

                                            Updated{" "}

                                            {lastUpdated.toLocaleTimeString(
                                                "en-IN",
                                                {
                                                    hour: "2-digit",
                                                    minute: "2-digit",
                                                }
                                            )}

                                        </small>

                                    )}

                                    <Button
                                        color="light"
                                        size="sm"
                                        onClick={loadDashboardData}
                                    >

                                        <i className="ri-refresh-line me-1"></i>

                                        Refresh

                                    </Button>

                                </div>

                            </div>

                        </Col>

                    </Row>


                    {/* =================================================
                        ERROR
                    ================================================= */}

                    {error && (

                        <Row>

                            <Col xs={12}>

                                <Alert color="danger">

                                    <i className="ri-error-warning-line me-1"></i>

                                    {error}

                                    <Button
                                        color="danger"
                                        outline
                                        size="sm"
                                        className="ms-2"
                                        onClick={loadDashboardData}
                                    >
                                        Try Again
                                    </Button>

                                </Alert>

                            </Col>

                        </Row>

                    )}


                    {/* =================================================
                        STAT CARDS
                    ================================================= */}

                    <Row className="crm-dashboard-stats">

                        {stats.map((item, index) => (

                            <Col
                                xs={6}
                                md={6}
                                xl={3}
                                key={index}
                            >

                                <Card className="crm-dashboard-stat-card">

                                    <CardBody>

                                        <div className="d-flex align-items-center">

                                            <div
                                                className={`avatar-sm rounded-circle bg-${item.className}-subtle`}
                                            >

                                                <span
                                                    className={`avatar-title rounded-circle bg-${item.className}-subtle text-${item.className} fs-20`}
                                                >

                                                    <i
                                                        className={
                                                            item.icon
                                                        }
                                                    ></i>

                                                </span>

                                            </div>

                                            <div className="ms-3">

                                                <p className="text-muted mb-1">
                                                    {item.title}
                                                </p>

                                                <h4 className="mb-0">
                                                    {item.value}
                                                </h4>

                                            </div>

                                        </div>

                                    </CardBody>

                                </Card>

                            </Col>

                        ))}

                    </Row>


                    {/* =================================================
                        CHARTS
                    ================================================= */}

                    <Row>

                        {/* MONTHLY LEADS */}

                        <Col
                            xl={8}
                            lg={7}
                            md={12}
                        >

                            <Card>

                                <CardHeader>

                                    <div>

                                        <h5 className="card-title mb-1">
                                            Lead Overview
                                        </h5>

                                        <p className="text-muted mb-0">
                                            Monthly leads created in{" "}
                                            {currentYear}
                                        </p>

                                    </div>

                                </CardHeader>

                                <CardBody>

                                    {leads.length === 0 ? (

                                        <div className="text-center text-muted py-5">

                                            No lead data available.

                                        </div>

                                    ) : (

                                        <Chart
                                            options={
                                                monthlyChart.options
                                            }
                                            series={
                                                monthlyChart.series
                                            }
                                            type="area"
                                            height={320}
                                        />

                                    )}

                                </CardBody>

                            </Card>

                        </Col>


                        {/* STATUS */}

                        <Col
                            xl={4}
                            lg={5}
                            md={12}
                        >

                            <Card>

                                <CardHeader>

                                    <h5 className="card-title mb-1">
                                        Lead Status
                                    </h5>

                                    <p className="text-muted mb-0">
                                        Current lead distribution
                                    </p>

                                </CardHeader>

                                <CardBody>

                                    {statusDistribution.length === 0 ? (

                                        <div className="text-center text-muted py-5">

                                            No status data available.

                                        </div>

                                    ) : (

                                        <Chart
                                            options={
                                                statusChart.options
                                            }
                                            series={
                                                statusChart.series
                                            }
                                            type="donut"
                                            height={320}
                                        />

                                    )}

                                </CardBody>

                            </Card>

                        </Col>

                    </Row>


                    {/* =================================================
                        STATUS SUMMARY
                    ================================================= */}

                    <Row>

                        <Col xs={12}>

                            <Card>

                                <CardHeader>

                                    <h5 className="card-title mb-1">
                                        Lead Statistics
                                    </h5>

                                    <p className="text-muted mb-0">
                                        Current CRM status summary
                                    </p>

                                </CardHeader>

                                <CardBody>

                                    <Row>

                                        {statusDistribution.map(
                                            ([status, count]) => (

                                                <Col
                                                    xs={6}
                                                    md={4}
                                                    xl={2}
                                                    key={status}
                                                    className="mb-3"
                                                >

                                                    <div className="border rounded p-3 h-100">

                                                        <div className="text-muted small mb-1">
                                                            {status}
                                                        </div>

                                                        <h4 className="mb-0">
                                                            {count}
                                                        </h4>

                                                    </div>

                                                </Col>

                                            )
                                        )}

                                    </Row>

                                </CardBody>

                            </Card>

                        </Col>

                    </Row>


                    {/* =================================================
                        RECENT LEADS
                    ================================================= */}

                    <Row>

                        <Col xs={12}>

                            <Card>

                                <CardHeader>

                                    <div className="d-flex justify-content-between align-items-center">

                                        <div>

                                            <h5 className="card-title mb-1">
                                                Recent Leads
                                            </h5>

                                            <p className="text-muted mb-0">
                                                Latest leads from CRM
                                            </p>

                                        </div>

                                        <span className="badge bg-primary-subtle text-primary">

                                            {leads.length} Total

                                        </span>

                                    </div>

                                </CardHeader>

                                <CardBody>

                                    {recentLeads.length === 0 ? (

                                        <div className="text-center text-muted py-4">

                                            No leads found.

                                        </div>

                                    ) : (

                                        <div className="table-responsive">

                                            <Table
                                                className="table align-middle table-nowrap mb-0"
                                            >

                                                <thead className="table-light">

                                                    <tr>

                                                        <th>
                                                            Lead
                                                        </th>

                                                        <th>
                                                            Company
                                                        </th>

                                                        <th>
                                                            Email
                                                        </th>

                                                        <th>
                                                            Phone
                                                        </th>

                                                        <th>
                                                            Course
                                                        </th>

                                                        <th>
                                                            Status
                                                        </th>

                                                        <th>
                                                            Created
                                                        </th>

                                                    </tr>

                                                </thead>

                                                <tbody>

                                                    {recentLeads.map(
                                                        (lead, index) => {

                                                            const name =
                                                                getLeadName(
                                                                    lead
                                                                );

                                                            const status =
                                                                getLeadStatus(
                                                                    lead
                                                                );

                                                            return (

                                                                <tr
                                                                    key={
                                                                        lead?.id ||
                                                                        lead?.leadid ||
                                                                        index
                                                                    }
                                                                >

                                                                    <td>

                                                                        <div className="d-flex align-items-center">

                                                                            <div className="avatar-xs me-2">

                                                                                <span className="avatar-title rounded-circle bg-primary-subtle text-primary">

                                                                                    {name
                                                                                        .charAt(
                                                                                            0
                                                                                        )
                                                                                        .toUpperCase()}

                                                                                </span>

                                                                            </div>

                                                                            <span className="fw-medium">

                                                                                {name}

                                                                            </span>

                                                                        </div>

                                                                    </td>

                                                                    <td>
                                                                        {getLeadCompany(
                                                                            lead
                                                                        )}
                                                                    </td>

                                                                    <td>
                                                                        {getLeadEmail(
                                                                            lead
                                                                        )}
                                                                    </td>

                                                                    <td>
                                                                        {getLeadPhone(
                                                                            lead
                                                                        )}
                                                                    </td>

                                                                    <td>
                                                                        {getLeadCourse(
                                                                            lead
                                                                        )}
                                                                    </td>

                                                                    <td>

                                                                        <span
                                                                            className={`badge ${getStatusClass(
                                                                                status
                                                                            )}`}
                                                                        >

                                                                            {status}

                                                                        </span>

                                                                    </td>

                                                                    <td>

                                                                        {formatDate(
                                                                            getLeadDate(
                                                                                lead
                                                                            )
                                                                        )}

                                                                    </td>

                                                                </tr>

                                                            );

                                                        }
                                                    )}

                                                </tbody>

                                            </Table>

                                        </div>

                                    )}

                                </CardBody>

                            </Card>

                        </Col>

                    </Row>

                </Container>

            </div>

        </React.Fragment>
    );
};


export default PortalDashboard;